import { isFulfilled } from "@reduxjs/toolkit";
import { getIn, useFormik } from "formik";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY, DEFAULT_SEARCH_INPUT_LENGTH } from "../../constants/app";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import {
  EnquiryCentres,
  setUpApprovedCentresPayload,
} from "../../models/enquiry";
import { AUTH_ROUTE } from "../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../store/application/slice";
import { getAddressSuggestionsThunk } from "../../store/auth/thunks";
import { AddressSuggestion } from "../../store/auth/types";
import { resetCentrePicAction } from "../../store/superadmin/slice";
import {
  getEnquiryDetailsThunk,
  setUpApprovedCentresThunk,
} from "../../store/superadmin/thunks";
import { ICentreDetailsNew } from "../../store/superadmin/types";
import { createValidationSchema } from "../../utils/createValidationSchema";
import SaveNotification from "../SaveNotification";
import CustomButton from "../UI/Buttons/CustomButton";
import CircleTick from "../UI/Icons/CircleTick";
import LeftArrow from "../UI/Icons/LeftArrow";
import Checkbox from "../UI/Inputs/Checkbox";
import ImageUpload from "../UI/Inputs/ImageUpload";
import TextArea from "../UI/Inputs/TextArea";
import TextInput from "../UI/Inputs/TextInput";
import ErrorMessage from "../UI/Tooltips/ErrorMessage";

type TimingDetails = {
  enabled: boolean;
  day: string;
  start_time: string;
  end_time: string;
};

type TCentreProfileImages = {
  index: number;
  path: string;
  link: string;
};

const hoursOptions: { key: string; value: string }[] = [];
for (let hour = 0; hour < 24; hour++) {
  const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
  const period = hour < 12 ? "AM" : "PM";
  const key = hour.toString().padStart(2, "0");
  hoursOptions.push({
    key: `${key}:00`,
    value: `${formattedHour}:00${period}`,
  });
  hoursOptions.push({
    key: `${key}:30`,
    value: `${formattedHour}:30${period}`,
  });
}

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const initialTimings: TimingDetails[] = [
  { day: "Monday", enabled: false, start_time: "07:00", end_time: "18:00" },
  { day: "Tuesday", enabled: false, start_time: "07:00", end_time: "18:00" },
  { day: "Wednesday", enabled: false, start_time: "07:00", end_time: "18:00" },
  { day: "Thursday", enabled: false, start_time: "07:00", end_time: "18:00" },
  { day: "Friday", enabled: false, start_time: "07:00", end_time: "18:00" },
  { day: "Saturday", enabled: false, start_time: "07:00", end_time: "18:00" },
  { day: "Sunday", enabled: false, start_time: "07:00", end_time: "18:00" },
];
const SetupNewCentres: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const error = useSelector((state) => state.auth.error);
  const superAdminError = useSelector((state) => state.superadmin.error);
  const inProgress = useSelector((state) => state.superadmin.inProgress);
  const centreImage = useSelector(
    (state) => state.superadmin.centrePicImage?.profile_picture
  );
  const profileImagePath = useSelector(
    (state) => state.superadmin.centrePicImage?.photo_path
  );
  const getEnquiryDetails = useSelector(
    (state) => state.superadmin.enquiryDetails
  );
  const addressSuggestionArr = useSelector(
    (state) => state.auth.addressSuggestions
  );

  let SetupApprovedCentresCount = 0;
  SetupApprovedCentresCount =
    getEnquiryDetails?.enquiry.filter((centre) => centre.business_name)
      ?.length ?? 0;

  const [uploadValidationError, setUploadValidationError] = useState<
    string | null
  >(null);
  const [currentCentreId, setCurrentCentreId] = useState<number | null>(null);
  const [currentCentreIndex, setCurrentCentreIndex] = useState<number>(0);
  const [imagePaths, setImagePaths] = useState<TCentreProfileImages[]>([]);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [centres, setCentres] = useState<EnquiryCentres[]>([
    {
      business_name: "",
      website: "",
      phone: "",
      address: "",
      abn: "",
      placements: "",
      profile: "",
      expectations: "",
      first_name: "",
      last_name: "",
      email: "",
      user_phone: "",
      role_id: "",
      centre_invoice_email: "",
      timings: daysOfWeek.map(
        (loopDay): TimingDetails => ({
          day: loopDay,
          enabled: false,
          start_time: "07:00",
          end_time: "18:00",
        })
      ),
      transport: [],
      photo: "",
      photoLink: "",
    },
  ]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");
  const [addressSuggestions, setAddressSuggestions] = useState<
    AddressSuggestion[]
  >([]);
  const [hasAddressSelected, setHasAddressSelected] = useState<boolean>(false)

  // useEffect(() => {
  //   if (addressSuggestionArr.length > 0) {
  //     setAddressSuggestions(addressSuggestionArr);
  //   }
  // }, [addressSuggestionArr]);

  /**
   * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
   * The debounced value is used to trigger the API call for fetching candidates.
   */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
   * Handles the change in the search input by updating the `selectedKeyword` state.
   * It also resets the current page to 1 if the search input is empty or exceeds the default length.
   *
   */
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch)
    }
  }, [debouncedSearch]);

  // useEffect(() => {
  //   if(selectedKeyword !== ""){
  //     const handleAddressSuggestions = async(selectedKeyword: string) => {
  //       await dispatch(getAddressSuggestionsThunk(selectedKeyword));
  //     }

  //     handleAddressSuggestions(selectedKeyword)
  //   } else {
  //     dispatch(resetAddressSuggestionsListAction());
  //   }
  // }, [dispatch, selectedKeyword])

  const enquiryData = async () => {
    dispatch(showTransparentPreloaderAction());
    const enquiryIdString: string | null = localStorage.getItem("enquiry_id");
    const enquiryId: number =
      enquiryIdString !== null ? Number(enquiryIdString) : 0;
    await dispatch(getEnquiryDetailsThunk({ enquiry_id: enquiryId }));
    dispatch(hideTransparentPreloaderAction());
  };

  useEffect(() => {
    enquiryData();
  }, []);

  useEffect(() => {
    setFieldValue("review_id", getEnquiryDetails?.primary_contact_id);
    if (getEnquiryDetails) {
      // Simulate setting values during onload
      setCentres(
        getEnquiryDetails?.enquiry?.map((item) => ({
          business_name: item.business_name || "",
          website: item.website || "",
          phone: item.center_phone || "",
          address: item.address || "",
          first_name: item.first_name || "",
          last_name: item.last_name || "",
          email: item.email || "",
          user_phone: item.phone_number || "",
          abn: "",
          franchise: "",
          placements: "",
          profile: "",
          expectations: "",
          role_id: "",
          centre_invoice_email: "",
          timings: initialTimings,
          transport: [],
          photo: "",
          photoLink: "",
        }))
      );
    }
  }, [getEnquiryDetails]);

  const redirectToAllCentres = () => {
    navigate(AUTH_ROUTE.ADMIN_CENTERS);
  };

  /**
   * Handles the display and population of centre details into the form fields.
   *
   * @param centreData - The data object representing a centre, containing all relevant details.
   *
   * This function performs the following actions:
   * - Sets the current centre ID.
   * - Populates various form fields with the centre's details, such as business name, website, phone number, address, franchise ID, ABN, placements, profile, and expectations.
   * - Updates the transport details by mapping the transport data into a specific format and setting it in the form.
   * - Updates the operating hours by transforming the timings data into a structured format and setting it in the form.
   */
  const handleShowCentreDetail =
    (centreData: ICentreDetailsNew, index: number) => () => {
      dispatch(resetCentrePicAction())
      setCurrentCentreId(centreData.id);
      setCurrentCentreIndex(index);

      setCentres((prev) =>
        prev.map((centre, idx) =>
          idx === index ? { ...centre, ...centre } : centre
        )
      );
    };

  /**
   * Handles the change event for a checkbox corresponding to a specific day.
   * Toggles the enabled state of the day in the `hours` object. If the checkbox
   * is unchecked, the day is removed from the object. If the checkbox is checked,
   * the day is added with default start and end times.
   *
   * @param day - The name of the day (e.g., "Monday", "Tuesday") for which the checkbox state is being changed.
   */
  const handleCheckboxChange = (centreIndex: number, day: string) => {
    setCentres((prevCentres) =>
      prevCentres.map((centre, idx) =>
        idx === centreIndex
          ? {
              ...centre,
              timings: centre.timings.map((timing) =>
                timing.day === day
                  ? {
                      ...timing,
                      enabled: !timing.enabled,
                      ...(timing.enabled
                        ? {} // if disabling, no change
                        : { start_time: "07:00", end_time: "18:00" }), // if enabling, set defaults
                    }
                  : timing
              ),
            }
          : centre
      )
    );
  };

  /**
   * Handles the change of time for a specific day and updates the hours state.
   *
   * @param day - The day for which the time is being updated (e.g., "Monday").
   * @param type - The type of time being updated, either "start_time" or "end_time".
   * @param value - The new time value to be set.
   *
   * @remarks
   * If the `type` is "start_time", the function automatically updates the `end_time`
   * to the first available option from `getFilteredCloseOptions` based on the new start time.
   * If no options are available, it defaults to the provided `value`.
   *
   * @example
   * ```typescript
   * handleTimeChange("Monday", "start_time", "09:00");
   * ```
   */
  const handleTimeChange = (
    centreIndex: number,
    day: string,
    field: "start_time" | "end_time",
    value: string
  ) => {
    setCentres((prevCentres) =>
      prevCentres.map((centre, idx) =>
        idx === centreIndex
          ? {
              ...centre,
              timings: centre.timings.map((timing: TimingDetails) =>
                timing.day === day
                  ? {
                      ...timing,
                      [field]: value, // Dynamically update start_time or end_time
                    }
                  : timing
              ),
            }
          : centre
      )
    );
  };

  /**
   *
   * Parses a time string in the format "hh:mm AM/PM" and converts it to minutes.
   * @param time - The time string in the format "hh:mm AM/PM".
   * @returns
   */
  const parseTime = (time: string) => {
    const match = time.match(/(\d+):(\d+)\s(AM|PM)/);
    if (!match) return 0; // Return a default value if the match fails
    const [hour, minute, period] = match.slice(1);
    let h = parseInt(hour, 10);
    const m = parseInt(minute, 10);
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return h * 60 + m;
  };

  /**
   * Filters the closing time options based on the selected opening time.
   * @param openTime - The opening time string in the format "hh:mm AM/PM".
   * @returns
   */
  const getFilteredCloseOptions = (openTime: string) => {
    return hoursOptions.filter(
      (time) => parseTime(time.value) > parseTime(openTime)
    );
  };

  const filterTimesAfterStart = (
    times: { key: string; value: string }[],
    startTime: string
  ) => {
    return times.filter((time) => time.key > startTime);
  };

  const handleTransportChange = (centreIndex: number, mode: string) => {
    setCentres((prevCentres) =>
      prevCentres.map((centre, idx) =>
        idx === centreIndex
          ? {
              ...centre,
              transport: centre.transport.some((t) => t.mode === mode)
                ? centre.transport.filter((t) => t.mode !== mode) // Remove if exists
                : [...centre.transport, { mode }], // Add new mode as TransportDetails
            }
          : centre
      )
    );
  };

  const handleCarParkingTransportChange = (
    centreIndex: number,
    mode: string,
    parking: number
  ) => {
    setCentres((prevCentres) =>
      prevCentres.map((centre, idx) =>
        idx === centreIndex
          ? {
              ...centre,
              transport: centre.transport.map((t) =>
                t.mode === mode ? { ...t, parking_on_site: parking } : t
              ),
            }
          : centre
      )
    );
  };

  const handleCarParkingSurroundChange = (
    centreIndex: number,
    mode: string,
    parkingSurround: number
  ) => {
    setCentres((prevCentres) =>
      prevCentres.map((centre, idx) =>
        idx === centreIndex
          ? {
              ...centre,
              transport: centre.transport.map((t) =>
                t.mode === mode
                  ? { ...t, parking_on_surrounding: parkingSurround }
                  : t
              ),
            }
          : centre
      )
    );
  };

  /**
   * Custom hook using `useFormik` to manage the form state and validation for centre details.
   *
   * @constant
   * @property {Function} handleSubmit - Function to handle form submission.
   * @property {Object} values - Current values of the form fields.
   * @property {Object} errors - Validation errors for the form fields.
   * @property {Object} touched - Tracks which fields have been touched.
   * @property {Function} setFieldValue - Function to programmatically set the value of a specific field.
   *
   * @typedef {Object} InitialValues
   * @property {string} review_id - Enquiry ID.
   * @property {string} business_details - Centre Details got from the enquiry.
   *
   * @function onSubmit
   * @async
   * @param {InitialValues} values - Form values to be submitted.
   * @description Handles the form submission, dispatches actions to update centre details, and provides feedback to the user.
   * @throws {Error} Throws an error if the service connection fails.
   */
  const { handleSubmit, errors, touched, setFieldValue } = useFormik({
    validationSchema: createValidationSchema("setUpApprovedCentres"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      review_id: 0,
      business_details: [
        {
          timings: initialTimings,
        },
      ] as EnquiryCentres[],
    },
    onSubmit: async (values: setUpApprovedCentresPayload) => {
      try {
        const payload = { ...values };
        const result = await dispatch(setUpApprovedCentresThunk(payload));

        if (isFulfilled(result)) {
          localStorage.removeItem("enquiry_id");
          setShowNotification(true);
          setTimeout(() => {
            navigate(AUTH_ROUTE.ADMIN_CENTERS);
          }, 3000);
        } else {
          toast.warn("Centre details updated failed");
        }
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
    },
  });

  const handleAddressSuggestions = async (selectedKeyword: string) => {
    const response = await dispatch(getAddressSuggestionsThunk(selectedKeyword));
    
    if (isFulfilled(response)) {
      setAddressSuggestions(response.payload.data);
    } else if (searchInput.length === 0) {
      setAddressSuggestions([]);
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleNestedChange = async (
    centreIndex: number,
    key: keyof EnquiryCentres,
    value: string,
    addressSelected?: boolean
  ) => {
    setCentres((prev) =>
      prev.map((centre, index) =>
        index === centreIndex ? { ...centre, [key]: value } : centre
      )
    );

    setHasAddressSelected(false)
    if (key === "address" && value.length > 2 && !addressSelected) {
      handleAddressSuggestions(value)
    }
  };

  const handleSelectAddress = (index: number, selectedAddress: string) => {
    handleNestedChange(index, "address", selectedAddress, true);
    setAddressSuggestions([]); // Hide dropdown after selection
    setSelectedKeyword("")
  };

  useEffect(() => {
    if (profileImagePath !== "") {
      setImagePaths((prevPaths) => {
        const exists = prevPaths.some(
          (item) =>
            item.index === currentCentreIndex && item.path === profileImagePath
        );
        if (!exists) {
          return [
            ...prevPaths.filter((item) => item.path !== undefined),
            {
              index: currentCentreIndex,
              path: profileImagePath,
              link: centreImage,
            },
          ];
        }
        return prevPaths;
      });
    }
  }, [profileImagePath, currentCentreIndex, centreImage]);

  useEffect(() => {
    const updateCentresWithPhotos = (imagePaths: TCentreProfileImages[]) => {
      setCentres((prevCentres) =>
        prevCentres.map((centre, index) => {
          const imagePath = imagePaths.find((img) => img.index === index); // Find the corresponding image path
          return {
            ...centre,
            photo: imagePath?.path || centre.photo, // Update photo if path exists
            photoLink: imagePath?.link || "",
          };
        })
      );
    };

    updateCentresWithPhotos(imagePaths);
  }, [imagePaths]);

  useEffect(() => {
    if (centres.length > 0) {
      setFieldValue("business_details", centres);
    }
  }, [centres, setFieldValue]);

  return (
    <>
      {showNotification && (
        <SaveNotification
          message={"Finished and Invite Sent"}
          onClose={() => setShowNotification(false)}
        />
      )}

      <div className="d-flex flex-column flex-lg-row gap-50 gap-mobile-20">
        <div className="d-flex flex-column gap-20 min-w-210px w-210px mobile-w-100 text-break">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-off-white text-black"
            onClick={redirectToAllCentres}
          >
            <LeftArrow />
            Back to All Centres
          </CustomButton>
          <div className="w-100 h-1px bg-off-white"></div>
          <div className="d-flex gap-10 align-items-center justify-content-between">
            <div className="fs-16 fw-700 text-teal-dark cursor-pointer">
              {SetupApprovedCentresCount > 0 ? SetupApprovedCentresCount : 0}{" "}
              Centres
            </div>
          </div>
          {getEnquiryDetails?.enquiry.map(
            (centreData: ICentreDetailsNew, index: number) => (
              <div
                key={index}
                className={`fs-16 text-black cursor-pointer ${
                  index === currentCentreIndex && "fw-700"
                }`}
                onClick={handleShowCentreDetail(centreData, index)}
              >
                {centreData.business_name}
              </div>
            )
          )}
        </div>
        <div className="w-100 h-1px bg-off-white d-block d-lg-none"></div>
        <div
          key={currentCentreIndex}
          className="border-left border-off-white pl-50px w-100 mobile-p-0 mobile-border-0"
        >
          <div className="d-flex flex-column gap-20 max-w-650px">
            <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center justify-content-lg-between w-100">
              <div className="fs-30 fw-400 text-black text-break">
                {centres[currentCentreIndex]?.business_name
                  ? centres[currentCentreIndex]?.business_name
                  : ""}
              </div>
              <div className="d-flex gap-10 align-items-center text-nowrap min-w-295px">
                <CustomButton
                  variant={"primary"}
                  isLoading={inProgress}
                  contentClass="d-flex"
                  type="button"
                  className="btn-theme bg-teal text-black mobile-w-100"
                  onClick={handleSubmit}
                >
                  <CircleTick fill={inProgress ? "#aaaaaa" : "#000000"} />
                  Finish Setup and Invite Admin
                </CustomButton>
              </div>
            </div>
            <div className="d-flex flex-column flex-md-row flex-wrap gap-30 align-items-center">
              <div className="d-flex flex-column order-2 order-md-1 gap-20">
                <ImageUpload
                  Apierror={error && error?.errors?.image?.[0]}
                  setUploadValidationError={setUploadValidationError}
                  type="centre-image-admin"
                  centreId={currentCentreId}
                  previewImage={centres[currentCentreIndex].photoLink}
                />
              </div>
              <div className="w-100 order-3">
                {(errors || uploadValidationError) && (
                  <ErrorMessage
                    message={
                      // (errors.business_details as string) ||
                      uploadValidationError as string
                    }
                  />
                )}
              </div>
              {/* <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6 max-w-250px mobile-w-100 order-1 order-md-2">
                <Warning />
                <div className="fs-13 fw-400 text-black lh-sm">
                  Your profile picture is visible to candidates. A high quality
                  image is recommended.
                </div>
              </div> */}
            </div>
            <div className="d-flex flex-column gap-10">
              <div className="d-flex flex-column gap-10">
                <div className="d-flex flex-column flex-lg-row gap-10">
                  <div className="flex-fill">
                    <TextInput
                      key={`centre-${currentCentreIndex}`}
                      value={centres[currentCentreIndex]?.business_name || ""}
                      field={`business_details[${currentCentreIndex}].business_name`}
                      label={"Business Name*"}
                      onChange={(e) =>
                        handleNestedChange(
                          currentCentreIndex,
                          "business_name",
                          e.target.value
                        )
                      }
                      touched={getIn(
                        touched,
                        `business_details[${currentCentreIndex}].business_name`
                      )}
                      error={getIn(
                        errors,
                        `business_details[${currentCentreIndex}].business_name`
                      )}
                      placeholder={" " + ""}
                    />
                  </div>
                  <div className="flex-fill">
                    <TextInput
                      key={`centre-${currentCentreIndex}`}
                      value={centres[currentCentreIndex]?.website || ""}
                      field={`business_details[${currentCentreIndex}].website`}
                      label={"Website*"}
                      onChange={(e) =>
                        handleNestedChange(
                          currentCentreIndex,
                          "website",
                          e.target.value
                        )
                      }
                      touched={getIn(
                        touched,
                        `business_details[${currentCentreIndex}].website`
                      )}
                      error={getIn(
                        errors,
                        `business_details[${currentCentreIndex}].website`
                      )}
                      placeholder={" " + ""}
                    />
                  </div>
                </div>
                <div className="d-flex flex-column flex-lg-row gap-10">
                  <div className="w-180px mobile-w-100">
                    <TextInput
                      key={`centre-${currentCentreIndex}`}
                      value={centres[currentCentreIndex]?.phone || ""}
                      field={`business_details[${currentCentreIndex}].phone`}
                      label={"Phone Number*"}
                      onChange={(e) =>
                        handleNestedChange(
                          currentCentreIndex,
                          "phone",
                          e.target.value
                        )
                      }
                      touched={getIn(
                        touched,
                        `business_details[${currentCentreIndex}].phone`
                      )}
                      error={getIn(
                        errors,
                        `business_details[${currentCentreIndex}].phone`
                      )}
                      placeholder={" " + ""}
                    />
                  </div>
                  <div className="flex-fill position-relative">
                    <TextInput
                      key={`centre-${currentCentreIndex}`}
                      value={centres[currentCentreIndex]?.address || ""}
                      field={`business_details[${currentCentreIndex}].address`}
                      label={"Address*"}
                      onChange={(e) =>
                        handleNestedChange(
                          currentCentreIndex,
                          "address",
                          e.target.value
                        )
                      }
                      touched={getIn(
                        touched,
                        `business_details[${currentCentreIndex}].address`
                      )}
                      error={getIn(
                        errors,
                        `business_details[${currentCentreIndex}].address`
                      )}
                      placeholder={" " + ""}
                    />
                    {addressSuggestions.length > 0 ? (
                      <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100">
                        {addressSuggestions.map((addr, i) => (
                          <div
                            key={i}
                            className="px-15px py-8px d-flex align-items-center cursor-pointer hover-bg-off-white text-break"
                            onClick={() =>
                              handleSelectAddress(
                                currentCentreIndex,
                                addr.description
                              )
                            }
                          >
                            {addr.description}
                          </div>
                        ))}
                      </div>
                    )
                    : (selectedKeyword.length > 0 && addressSuggestions.length === 0) ? 
                      <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100 py-10px pl-10px">No Address Found</div>
                    : "" }
                  </div>

                 
                </div>
                <div className="d-flex flex-column flex-lg-row gap-10">
                  <div className="flex-fill">
                    <TextInput
                      key={`centre-${currentCentreIndex}`}
                      value={centres[currentCentreIndex]?.abn || ""}
                      field={`business_details[${currentCentreIndex}].abn`}
                      label={"ABN"}
                      onChange={(e) =>
                        handleNestedChange(
                          currentCentreIndex,
                          "abn",
                          e.target.value
                        )
                      }
                      touched={getIn(
                        touched,
                        `business_details[${currentCentreIndex}].abn`
                      )}
                      error={getIn(
                        errors,
                        `business_details[${currentCentreIndex}].abn`
                      )}
                      placeholder={" " + ""}
                    />
                  </div>
                  <div className="flex-fill">
                    <TextInput
                      key={`centre-${currentCentreIndex}`}
                      value={centres[currentCentreIndex]?.placements || ""}
                      field={`business_details[${currentCentreIndex}].placements`}
                      label={"Licensed places"}
                      onChange={(e) =>
                        handleNestedChange(
                          currentCentreIndex,
                          "placements",
                          e.target.value
                        )
                      }
                      touched={getIn(
                        touched,
                        `business_details[${currentCentreIndex}].placements`
                      )}
                      error={getIn(
                        errors,
                        `business_details[${currentCentreIndex}].placements`
                      )}
                      placeholder={" " + ""}
                    />
                  </div>
                </div>

                 <div className="d-flex flex-column flex-lg-row gap-10">
                    <div className="flex-fill">
                      <TextInput
                        field={`business_details[${currentCentreIndex}].centre_invoice_email`}
                        label={"Email Address (To be used for invoicing)"}
                        onChange={(e) =>
                        handleNestedChange(
                          currentCentreIndex,
                          "centre_invoice_email",
                          e.target.value
                        )
                      }
                       touched={getIn(
                        touched,
                        `business_details[${currentCentreIndex}].centre_invoice_email`
                      )}
                       
                        error={getIn(
                        errors,
                        `business_details[${currentCentreIndex}].centre_invoice_email`
                      )}
                        placeholder={" "}
                        value={centres[currentCentreIndex]?.centre_invoice_email || ""}
                      />
                      </div>
                    </div>
              </div>
            </div>
            <div className="d-flex flex-column gap-20">
              <div className="fs-16 fw-400 text-black">
                Invite primary contact to join as a Centre Admin:
              </div>
              <div className="d-flex flex-column gap-10">
                <div className="d-flex flex-column flex-lg-row gap-10">
                  <div className="flex-fill">
                    <TextInput
                      value={centres[currentCentreIndex]?.first_name || ""}
                      field={`business_details[${currentCentreIndex}].first_name`}
                      label={"First Name*"}
                      onChange={(e) =>
                        handleNestedChange(
                          currentCentreIndex,
                          "first_name",
                          e.target.value
                        )
                      }
                      touched={getIn(
                        touched,
                        `business_details[${currentCentreIndex}].first_name`
                      )}
                      error={getIn(
                        errors,
                        `business_details[${currentCentreIndex}].first_name`
                      )}
                      placeholder={" " + ""}
                    />
                  </div>
                  <div className="flex-fill">
                    <TextInput
                      value={centres[currentCentreIndex]?.last_name || ""}
                      field={`business_details[${currentCentreIndex}].last_name`}
                      label={"Last Name*"}
                      onChange={(e) =>
                        handleNestedChange(
                          currentCentreIndex,
                          "last_name",
                          e.target.value
                        )
                      }
                      touched={getIn(
                        touched,
                        `business_details[${currentCentreIndex}].last_name`
                      )}
                      error={getIn(
                        errors,
                        `business_details[${currentCentreIndex}].last_name`
                      )}
                      placeholder={" " + ""}
                    />
                  </div>
                </div>
                <div className="d-flex flex-column flex-lg-row gap-10">
                  <div className="flex-fill">
                    <TextInput
                      value={centres[currentCentreIndex]?.email || ""}
                      field={`business_details[${currentCentreIndex}].email`}
                      label={"Email Address*"}
                      onChange={(e) =>
                        handleNestedChange(
                          currentCentreIndex,
                          "email",
                          e.target.value
                        )
                      }
                      touched={getIn(
                        touched,
                        `business_details[${currentCentreIndex}].email`
                      )}
                      error={getIn(
                        errors,
                        `business_details[${currentCentreIndex}].email`
                      )}
                      placeholder={" " + ""}
                    />
                    {superAdminError && (
                      <ErrorMessage message={superAdminError?.errors?.email?.[0]} />
                    )}
                  </div>
                  <div className="flex-fill">
                    <TextInput
                      value={centres[currentCentreIndex]?.user_phone || ""}
                      field={`business_details[${currentCentreIndex}].user_phone`}
                      label={"Phone Number*"}
                      onChange={(e) =>
                        handleNestedChange(
                          currentCentreIndex,
                          "user_phone",
                          e.target.value
                        )
                      }
                      touched={getIn(
                        touched,
                        `business_details[${currentCentreIndex}].user_phone`
                      )}
                      error={getIn(
                        errors,
                        `business_details[${currentCentreIndex}].user_phone`
                      )}
                      placeholder={" " + ""}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="fs-22 fw-400 text-black">Opening Hours</div>
            <div className="d-flex flex-column gap-8">
              {daysOfWeek.map((day: string, index: number) => {
                const centre = centres[currentCentreIndex];
                const timingForDay = centre
                  ? centre.timings.find((t) => t.day === day)
                  : undefined;
                return (
                  <div
                    key={day ?? index}
                    className="d-flex gap-5 align-items-center"
                  >
                    <div className="theme-check min-w-180px">
                      <Checkbox
                        className="theme-check-input"
                        id={day}
                        checked={timingForDay?.enabled || false}
                        onChange={() =>
                          handleCheckboxChange(currentCentreIndex, day)
                        }
                        label={day}
                        themeCheck={true}
                        labelBold={true}
                      />
                    </div>
                    {timingForDay?.enabled ? (
                      <>
                        <div className="form-group flex-fill">
                          <select
                            className="form-select"
                            value={timingForDay?.start_time}
                            onChange={(e) =>
                              handleTimeChange(
                                currentCentreIndex,
                                day,
                                "start_time",
                                e.target.value
                              )
                            }
                          >
                            {hoursOptions.map((time) => (
                              <option key={time.key} value={time.key}>
                                {time.value}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="fs-16 fw-400 text-black">-</div>
                        <div className="form-group flex-fill">
                          <select
                            className="form-select"
                            value={timingForDay?.end_time}
                            onChange={(e) =>
                              handleTimeChange(
                                currentCentreIndex,
                                day,
                                "end_time",
                                e.target.value
                              )
                            }
                          >
                            {filterTimesAfterStart(
                              hoursOptions,
                              timingForDay?.start_time
                            ).map((time) => (
                              <option key={time.key} value={time.key}>
                                {time.value}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    ) : (
                      <div className="flex-fill fs-16 fw-400 text-black lh-40px">
                        Closed
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="fs-22 fw-400 text-black">Centre Profile</div>
            <TextArea
              value={centres[currentCentreIndex]?.profile || ""}
              field={`business_details[${currentCentreIndex}].profile`}
              placeholder={"Enter an introduction..."}
              onChange={(e) =>
                handleNestedChange(
                  currentCentreIndex,
                  "profile",
                  e.target.value
                )
              }
              touched={getIn(
                touched,
                `business_details[${currentCentreIndex}].profile`
              )}
              error={getIn(
                errors,
                `business_details[${currentCentreIndex}].profile`
              )}
              // placeholder={" " + ""}
            />
            <div className="fs-22 fw-400 text-black">Centre Expectations</div>
            <TextArea
              value={centres[currentCentreIndex]?.expectations || ""}
              field={`business_details[${currentCentreIndex}].expectations`}
              placeholder={"Enter details..."}
              onChange={(e) =>
                handleNestedChange(
                  currentCentreIndex,
                  "expectations",
                  e.target.value
                )
              }
              touched={getIn(
                touched,
                `business_details[${currentCentreIndex}].expectations`
              )}
              error={getIn(
                errors,
                `business_details[${currentCentreIndex}].expectations`
              )}
              // placeholder={" " + ""}
            />
            <div className="fs-22 fw-400 text-black">Transport</div>
            <div className="fs-16 fw-400 text-black">
              Which mode/s of transport and parking locations are available
              nearby your centre?{" "}
            </div>
            <div className="d-flex flex-column gap-10">
              <div className="theme-check">
                <Checkbox
                  className="theme-check-input"
                  id="transport_car"
                  checked={
                    centres[currentCentreIndex]?.transport.some(
                      (t) => t.mode === "car"
                    ) || false
                  } // Check if 'mode' is 'car'
                  onChange={() =>
                    handleTransportChange(currentCentreIndex, "car")
                  }
                  label={"Car"}
                  themeCheck={true}
                />
              </div>
              <div className="d-flex flex-column gap-10 pl-50px">
                <div className="theme-check">
                  <Checkbox
                    className="theme-check-input"
                    id="car_parking_site"
                    checked={
                      centres[currentCentreIndex]?.transport.some(
                        (t) => t.mode === "car" && t.parking_on_site === 1
                      ) || false
                    }
                    onChange={(checked: boolean) =>
                      handleCarParkingTransportChange(
                        currentCentreIndex,
                        "car",
                        checked ? 1 : 0
                      )
                    }
                    label={"Parking is available on site"}
                    themeCheck={true}
                  />
                </div>
                <div className="theme-check">
                  <Checkbox
                    className="theme-check-input"
                    id="car_parking_surrounding"
                    checked={
                      centres[currentCentreIndex]?.transport.some(
                        (t) =>
                          t.mode === "car" && t.parking_on_surrounding === 1
                      ) || false
                    }
                    onChange={(checked: boolean) =>
                      handleCarParkingSurroundChange(
                        currentCentreIndex,
                        "car",
                        checked ? 1 : 0
                      )
                    }
                    label={"Parking is available on surrounding streets"}
                    themeCheck={true}
                  />
                </div>
              </div>
              <div className="theme-check">
                <Checkbox
                  className="theme-check-input"
                  id="transport_bus"
                  checked={
                    centres[currentCentreIndex]?.transport.some(
                      (t) => t.mode === "bus"
                    ) || false
                  } // Check if 'mode' is 'car'
                  onChange={() =>
                    handleTransportChange(currentCentreIndex, "bus")
                  }
                  label={"Bus"}
                  themeCheck={true}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  className="theme-check-input"
                  id="transport_train"
                  checked={
                    centres[currentCentreIndex]?.transport.some(
                      (t) => t.mode === "train"
                    ) || false
                  } // Check if 'mode' is 'car'
                  onChange={() =>
                    handleTransportChange(currentCentreIndex, "train")
                  }
                  label={"Train"}
                  themeCheck={true}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  className="theme-check-input"
                  id="transport_tram"
                  checked={
                    centres[currentCentreIndex]?.transport.some(
                      (t) => t.mode === "tram"
                    ) || false
                  } // Check if 'mode' is 'car'
                  onChange={() =>
                    handleTransportChange(currentCentreIndex, "tram")
                  }
                  label={"Tram"}
                  themeCheck={true}
                />
              </div>
            </div>
            <div className="fs-16 fw-400 text-black">
              Please only select the above public transportation if it is less
              than 15min walk away.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SetupNewCentres;
